import { useState, useEffect } from 'react';
import { DOMAIN_NAME } from 'constants/index';

type Props = {
  src: string,
  width?: number,
  height?: number,
}

type ImageResponse = {
  currentSrc: string,
}

/**
 * Query parameter that asks the service worker (public/serviceWorker.js) to
 * load an image through the API and media server instead of from the site.
 * The service worker defines the same name; keep the two in step.
 */
export const MEDIA_PROXY_PARAM = 'epub_media_proxy';

/**
 * Site media and gallery paths. jade resizes these on request (?width=,
 * ?height=), caches the result, and serves them through the site's Varnish.
 */
const SITE_MEDIA_PATH = /^\/ext\/(resources|galleries)\//;

/**
 * Whether an image URL takes our resize parameters: one of ours on an
 * epublishing.com host, or a site's own media or gallery file. Images whose
 * URLs point anywhere else are left alone.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isResizableUrl(url: string): boolean {
  if (url.indexOf(DOMAIN_NAME) !== -1) return true;

  try {
    return SITE_MEDIA_PATH.test(new URL(url, window.location.href).pathname);
  } catch {
    return false;
  }
}

/**
 * The same URL, marked for the service worker to load through the media
 * server.
 *
 * @param {string} url
 * @returns {string}
 */
export function withMediaProxyParam(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${MEDIA_PROXY_PARAM}=1`;
}

/**
 * <img onError> handler: retry an image once through the media server.
 * Production editors load site images straight from the site; a site that
 * doesn't serve one (not live yet, missing file) gets this second try.
 *
 * @param {React.SyntheticEvent<HTMLImageElement>} event
 * @returns {boolean} true if it retried, false if the retry already failed
 */
export function retryThroughMediaProxy(event: { currentTarget: HTMLImageElement }): boolean {
  const img = event.currentTarget;
  // Judged by the src itself rather than a flag on the element: React reuses
  // <img> nodes for new images, and each new src gets its own retry.
  if (img.src.includes(`${MEDIA_PROXY_PARAM}=1`)) return false;

  img.src = withMediaProxyParam(img.src);
  return true;
}

/**
 * Check if passed new url and old url are matched without timestamp
 * @param {string} url
 */
function isUrlMatched (src: string, url: string) {
  const srcParts = src.split(".");
  const srcFirstPart = srcParts.slice(0, srcParts.length - 1);
  const urlParts = url.split(".");
  const urlFirstPart = urlParts.slice(0, urlParts.length - 1);

  // Does timestamp not exist ?
  if (!urlFirstPart.length || !srcFirstPart.length) return false;

  // Not a replaced file url
  if (urlFirstPart.join('.') !== srcFirstPart.join('.')) return false;

  return true;
}

/**
 * Attach current timestamp to src
 * @param {string} url
 * @param {string} newTimestamp
 */
function getUrlWithCorrectTimestamp(url: string, newTimestamp: string): string {
  const urlParts = url.split("?");
  const queryStringParts = urlParts.length > 1 ? urlParts[1].split('&') : [];
  const queryStringWithoutTimestamp = queryStringParts.length > 1 ? queryStringParts.slice(1, queryStringParts.length).join('&') : "";

  return queryStringParts.length > 1 ? `${urlParts[0]}?${newTimestamp}&${queryStringWithoutTimestamp}` : `${urlParts[0]}?${newTimestamp}`;
}

/**
 * Image loading hook to show lazy loaded image
 *
 * @param {string} src
 * @param {number} width optional, provide image width, api will generate and return image of that size
 * @param {number} height optional, provide image height, api will generate and return image of that size
 */
export function useImage({src, width, height}: Props): ImageResponse {
  const [state, setState] = useState({
    currentSrc: "",
    newSrc: "",
  });
  const { currentSrc, newSrc } = state;

  /**
   * When an image is replaced, update its URL with a new timestamp so that it gets updated in the UI.
   */
  useEffect(() => {
    const handler = (event) => {
      const decodedUrl = event.detail.url;
      if (isUrlMatched(decodeURIComponent(src), decodedUrl)) {
        setState((prevState) => ({
          ...prevState,
          newSrc: encodeURI(getUrlWithCorrectTimestamp(decodedUrl, event.detail.timeStamp)),
        }));
      }
    };
    document.addEventListener('epub.replaceImgUrlAdded', handler);

    return () => document.removeEventListener('epub.replaceImgUrlAdded', handler);
  }, []);

  /**
   *  Update the source of the image with a size attribut.
   **/
  useEffect(() => {
    let mounted = true;

    /**
     * Set width or height resize attr on image URL, if not a remote image.
     *
     * @returns {string}
     */
    function updateSrc(): string {
      const srcInUse = newSrc ? newSrc : src;
      let srcModified = srcInUse.slice();
      const isRemoteUrl = !isResizableUrl(srcInUse);

      const join = (url: string) => (url.includes('?') ? '&' : '?');

      if (width && !isRemoteUrl) {
        srcModified = `${srcInUse}${join(srcInUse)}width=${width}`;
      }

      if (height && !isRemoteUrl) {
        srcModified = `${srcModified}${join(srcModified)}height=${height}`;
      }

      return srcModified;
    }

    if (mounted) setState((prevState) => ({ ...prevState, loading: false, currentSrc: updateSrc() }));

    return () => {
      mounted = false;
    };
  }, [src, newSrc]);

  return {
    currentSrc,
  };
}
